import Spinner from '@/presentation/components/spinner';
import { useIsSuperAdmin, useRBACContext } from '@/domain/context/rbac';
import useCampaignDetail from '@/presentation/hooks/firebase/use-campaign-detail';
import useCampaignMentions from '@/presentation/hooks/firebase/use-campaign-mentions';
import type { CampaignMention } from '@/presentation/hooks/firebase/use-campaign-mentions';
import { CampaignRanking } from '@/domain/types/campaigns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { exportMentionsToExcel } from '../utils/exportMentionsToExcel';

const COLLECTION_LABELS: Record<string, string> = {
  newsletters: 'Publicaciones',
  policies: 'Políticas',
  reports: 'Reportes',
  releases: 'Última Hora',
  critical_standars: 'Estándares Críticos',
  podcasts: 'Audios',
  videos: 'Videos',
  links: 'Enlaces',
  galleries: 'Galerías',
};

const formatName = (raw: string): string =>
  raw
    .split(' ')
    .filter((w) => w && w.toLowerCase() !== 'undefined')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ') || '—';

const MentionCard = ({ m }: { m: CampaignMention }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = m.comment.length > 120;

  return (
    <div className="border rounded-lg p-3 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
          {COLLECTION_LABELS[m.collectionName] ?? m.collectionName}
        </span>
        <span className="text-xs text-gray-400">
          {m.createdAt?.toDate().toLocaleDateString('es-CO', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      </div>
      <p className="font-medium text-sm text-gray-800">{m.publicationTitle || '(sin título)'}</p>
      <p className={`text-sm text-gray-500 ${!expanded && isLong ? 'line-clamp-2' : ''}`}>
        {m.comment}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-blue-500 hover:text-blue-700 self-start mt-0.5"
        >
          {expanded ? 'Ver menos' : 'Ver más'}
        </button>
      )}
    </div>
  );
};

const CampaignDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { campaign, rankings, totalRankingsCount, isLoading, fetchDetail, toggleActive, deleteCampaign } = useCampaignDetail(id ?? '');
  const { mentions, isLoading: isLoadingMentions, fetchByUser, clear } = useCampaignMentions(id ?? '');
  const { hasPermission, loading: rbacLoading } = useRBACContext();
  const [selectedUser, setSelectedUser] = useState<CampaignRanking | null>(null);
  const [isActioning, setIsActioning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [rankingPage, setRankingPage] = useState(1);
  const [rankingSearch, setRankingSearch] = useState('');
  const PAGE_SIZE = 10;

  // Super admin = puede gestionar roles (permiso que solo tienen los super admins)
  const isSuperAdmin = useIsSuperAdmin();
  const canDelete = !rbacLoading && (isSuperAdmin || (hasPermission('campaigns', 'delete') && totalRankingsCount === 0));
  const deleteBlocked = !rbacLoading && hasPermission('campaigns', 'delete') && !isSuperAdmin && totalRankingsCount > 0;

  const handleToggleActive = useCallback(async () => {
    setIsActioning(true);
    try { await toggleActive(); } finally { setIsActioning(false); }
  }, [toggleActive]);

  const handleDelete = useCallback(async () => {
    setIsActioning(true);
    try {
      await deleteCampaign();
      navigate('/dashboard/campaigns');
    } finally {
      setIsActioning(false);
      setShowDeleteConfirm(false);
    }
  }, [deleteCampaign, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const openMentions = useCallback(
    (ranking: CampaignRanking) => {
      setSelectedUser(ranking);
      fetchByUser(ranking.userId);
    },
    [fetchByUser],
  );

  const closeModal = useCallback(() => {
    setSelectedUser(null);
    clear();
  }, [clear]);

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (!campaign) {
    return <p className="p-4 text-red-500">Campaña no encontrada</p>;
  }

  const totalMenciones = rankings.reduce((acc, r) => acc + r.mentionCount, 0);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/campaigns')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Volver
          </button>
          <h1 className="text-3xl font-bold">{campaign.name}</h1>
          <span
            className={`text-sm px-3 py-1 rounded-full font-medium ${
              campaign.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {campaign.isActive ? 'Activa' : 'Inactiva'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/dashboard/campaigns/add?edit=${id}`)}
            className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-sm"
          >
            Editar
          </button>
          <button
            onClick={handleToggleActive}
            disabled={isActioning}
            className={`px-3 py-1.5 rounded-lg text-sm disabled:opacity-50 ${
              campaign.isActive
                ? 'border border-orange-300 text-orange-700 hover:bg-orange-50'
                : 'border border-green-300 text-green-700 hover:bg-green-50'
            }`}
          >
            {campaign.isActive ? 'Desactivar' : 'Activar'}
          </button>
          <div className="relative group">
            <button
              onClick={() => canDelete && setShowDeleteConfirm(true)}
              disabled={isActioning || !canDelete}
              className="border border-red-300 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Eliminar
            </button>
            {deleteBlocked && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 hidden group-hover:block z-10">
                Hay {totalRankingsCount} participante{totalRankingsCount !== 1 ? 's' : ''} en esta campaña. Solo el super administrador puede eliminarla.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg text-gray-900">¿Eliminar campaña?</h3>
            {totalRankingsCount > 0 ? (
              <p className="text-sm text-gray-600">
                Esta campaña tiene <span className="font-semibold text-red-600">{totalRankingsCount} participante{totalRankingsCount !== 1 ? 's' : ''}</span>. Se eliminarán también sus datos de ranking. Esta acción no se puede deshacer.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Se eliminará la campaña <span className="font-semibold">"{campaign.name}"</span>. Esta acción no se puede deshacer.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isActioning}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {isActioning ? 'Eliminando...' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border p-4 flex flex-col gap-2">
        <p>
          <span className="font-medium">Hashtag:</span>{' '}
          <span className="text-blue-600 font-semibold">{campaign.hashtag}</span>
        </p>
        {campaign.instructions && (
          <p>
            <span className="font-medium">Instrucciones:</span> {campaign.instructions}
          </p>
        )}
        <p>
          <span className="font-medium">Vigencia:</span>{' '}
          {campaign.startDate.toDate().toLocaleDateString('es-CO')} —{' '}
          {campaign.endDate.toDate().toLocaleDateString('es-CO')}
        </p>
        <div className="flex gap-6 mt-2">
          <div>
            <p className="text-gray-500 text-sm">Participantes</p>
            <p className="text-2xl font-bold">{rankings.length}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Total menciones</p>
            <p className="text-2xl font-bold">{totalMenciones}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Ranking de usuarios</h2>
        <button
          onClick={() => exportMentionsToExcel(rankings, campaign.name)}
          disabled={rankings.length === 0}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
        >
          Descargar Excel
        </button>
      </div>

      {rankings.length > 0 && (
        <input
          type="text"
          value={rankingSearch}
          onChange={(e) => { setRankingSearch(e.target.value); setRankingPage(1); }}
          placeholder="Buscar por nombre o número de documento..."
          className="border rounded-lg px-3 py-2 text-sm w-full max-w-sm"
        />
      )}

      {rankings.length === 0 ? (
        <p className="text-gray-400 py-4">Aún no hay menciones registradas.</p>
      ) : (() => {
        const query = rankingSearch.trim().toLowerCase();
        const filtered = query
          ? rankings.filter(
              (r) =>
                r.name.toLowerCase().includes(query) ||
                r.document.toLowerCase().includes(query),
            )
          : rankings;
        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        const pageRankings = filtered.slice((rankingPage - 1) * PAGE_SIZE, rankingPage * PAGE_SIZE);
        const globalOffset = (rankingPage - 1) * PAGE_SIZE;
        if (filtered.length === 0) {
          return <p className="text-gray-400 py-4">No se encontraron participantes con ese criterio.</p>;
        }
        return (
          <div className="flex flex-col gap-2">
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 font-medium">
                  <tr>
                    <th className="text-left px-4 py-3">Pos.</th>
                    <th className="text-left px-4 py-3">Nombre</th>
                    <th className="text-left px-4 py-3">Documento</th>
                    <th className="text-right px-4 py-3">Menciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRankings.map((r, i) => (
                    <tr key={r.userId} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-bold text-gray-400">#{globalOffset + i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.userPhoto && (
                            <img
                              src={r.userPhoto}
                              alt=""
                              className="w-7 h-7 rounded-full object-cover"
                            />
                          )}
                          <span>{formatName(r.name)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{r.document}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openMentions(r)}
                          className="font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          title="Ver publicaciones donde mencionó el hashtag"
                        >
                          {r.mentionCount}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-gray-500 px-1">
                <span>
                  {globalOffset + 1}–{Math.min(rankingPage * PAGE_SIZE, filtered.length)} de {filtered.length} participante{filtered.length !== 1 ? 's' : ''}{query ? ` (${rankings.length} total)` : ''}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setRankingPage((p) => Math.max(1, p - 1))}
                    disabled={rankingPage === 1}
                    className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Anterior
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setRankingPage(p)}
                      className={`w-8 h-8 rounded-lg text-sm ${
                        p === rankingPage
                          ? 'bg-blue-600 text-white font-medium'
                          : 'border hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setRankingPage((p) => Math.min(totalPages, p + 1))}
                    disabled={rankingPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Siguiente →
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Modal de menciones por usuario */}
      {selectedUser && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-bold text-lg">{formatName(selectedUser.name)}</h3>
                <p className="text-sm text-gray-500">
                  {selectedUser.mentionCount} mención{selectedUser.mentionCount !== 1 ? 'es' : ''} de{' '}
                  <span className="text-blue-600 font-medium">{campaign.hashtag}</span>
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4">
              {isLoadingMentions ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : mentions.length === 0 ? (
                <p className="text-gray-400 text-center py-8">
                  No hay menciones detalladas registradas para este usuario.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {mentions.map((m) => (
                    <MentionCard key={m.id} m={m} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetailPage;
